// Module 10 — the facility-parcel applier (P14). Substation/plant/switch footprints draw as a luminous
// hollow WallGeometry band PLUS a translucent filled floor (the same top-down treatment neighbourhoods
// get). Geometry is built ONCE (rings never change); per tick only the per-instance COLOUR mutates in
// place via getGeometryInstanceAttributes + ColorGeometryInstanceAttribute.toValue — no geometry rebuild,
// no FPS cliff (viz rule: primitives + in-place attribute mutation). This is the P14 "recolour parcels by
// condition for ALL node types" channel: at `normal` a parcel keeps its facility-type IDENTITY hue
// (plant orange / substation blue); a stressed/shortage/outage node OVERRIDES it with the CVD-safe
// condition ramp (matching the edge loading ramp). Extracted from the adapter's inline build so the
// recolour is testable in isolation (mirrors walls.ts). Boundary: same-module + domain only.
import { FOOTPRINT_ALPHA } from "./footprints";
import { assetConditionStyle } from "./effects/palette";
import type { AssetStatus } from "./effects/spec";
import type { CesiumLike, SceneLike, PrimitiveLike, GeometryInstanceLike, ColorLike } from "./cesium-api";

const WALL_TOP_M = 260; // lower than neighbourhood walls (600) — a local facility footprint
const WALL_ALPHA = 0.85; // the hollow band opacity at `normal`

// facility-parcel wall hue by `facType` — plants hot, substations magenta, switching amber (display
// only). This is the IDENTITY hue shown at `normal`; the condition recolour overrides it under stress.
// Substation is magenta (NOT blue) to stay distinguishable from the NUCLEAR/HYDRO plant-column blues
// (FUEL_COLOR in palette.ts) — a blue substation footprint next to a blue nuclear/hydro plant column
// read as "the same kind of thing" from a distance; magenta has no overlap with any FUEL_COLOR hue.
export const FAC_COLOR: Record<string, string> = {
  plant: "#ff7043", generation: "#ff7043", nuclear: "#ff5252", hydro: "#4dd0e1",
  solar: "#ffee58", wind: "#81d4fa", storage: "#26c6da", generator: "#ff8a65",
  substation: "#f06292", transformer: "#ab47bc", switch: "#ffd54f", portal: "#90a4ae",
};
export function facColor(facType: string): string {
  return FAC_COLOR[facType] ?? "#f06292";
}

// A resolved facility footprint: the REAL model node it represents (the adapter does the parcel→node
// crosswalk), its facility type (identity hue), and its perimeter ring.
export interface FacilityParcel {
  nodeId: string;
  facType: string;
  ring: ReadonlyArray<{ lat: number; lon: number }>;
}

export interface FacilityParcelLayer {
  apply(statusOf: (nodeId: string) => AssetStatus): void; // per tick: recolour each parcel by its node's condition
  setShow(visible: boolean): void; // perf-diagnosis lever (primitive.show — no rebuild)
  setModelMatrix(m: unknown): void; // 3D "float" lift — Matrix4 applied to both primitives
  dispose(): void;
}

interface Built {
  nodeId: string;
  pickId: { kind: "node"; id: string };
  baseHex: string;
}

// Build BOTH primitives (translucent floor UNDER the hollow wall band) ONCE from the resolved parcels,
// then recolour in place each tick. The fill + wall share ONE pickId per facility, so a click on either
// selects the real bus (and the recolour looks each up in its own primitive by that id reference).
export function makeFacilityParcelLayer(
  cesium: CesiumLike,
  scene: SceneLike,
  parcels: readonly FacilityParcel[],
): FacilityParcelLayer {
  let wallPrim: PrimitiveLike | null = null;
  let fillPrim: PrimitiveLike | null = null;
  const built: Built[] = [];

  const wallInstances: GeometryInstanceLike[] = [];
  const fillInstances: GeometryInstanceLike[] = [];
  for (const p of parcels) {
    if (p.ring.length < 3) continue;
    const flat: number[] = [];
    for (const c of p.ring) flat.push(c.lon, c.lat);
    const positions = cesium.Cartesian3.fromDegreesArray(flat);
    const baseHex = facColor(p.facType);
    const pickId = { kind: "node" as const, id: p.nodeId };
    built.push({ nodeId: p.nodeId, pickId, baseHex });
    wallInstances.push(new cesium.GeometryInstance({
      geometry: new cesium.WallGeometry({
        positions,
        maximumHeights: p.ring.map(() => WALL_TOP_M),
        minimumHeights: p.ring.map(() => 0),
      }),
      id: pickId, // pickable + identity-stable for the in-place recolour lookup
      attributes: { color: cesium.ColorGeometryInstanceAttribute.fromColor(cesium.Color.fromCssColorString(baseHex).withAlpha(WALL_ALPHA)) },
    }));
    fillInstances.push(new cesium.GeometryInstance({
      geometry: cesium.PolygonGeometry.fromPositions({ positions, height: 0 }),
      id: pickId, // SAME id as the band → consistent selection from any angle
      attributes: { color: cesium.ColorGeometryInstanceAttribute.fromColor(cesium.Color.fromCssColorString(baseHex).withAlpha(FOOTPRINT_ALPHA)) },
    }));
  }

  if (built.length > 0) {
    // floor FIRST (added to the scene before the band ⇒ the band/points stay on top for clicks, the
    // floor is still drill-pickable for a top-down click). Same as the neighbourhood walls + footprints.
    fillPrim = new cesium.Primitive({
      geometryInstances: fillInstances,
      appearance: new cesium.PerInstanceColorAppearance({ flat: true, translucent: true }),
      asynchronous: true,
    });
    scene.primitives.add(fillPrim);
    wallPrim = new cesium.Primitive({
      geometryInstances: wallInstances,
      appearance: new cesium.PerInstanceColorAppearance({ flat: true, translucent: true }),
      asynchronous: true, // off-thread build, same as the neighbourhood walls
    });
    scene.primitives.add(wallPrim);
  }

  const recolour = (prim: PrimitiveLike, b: Built, hex: string, alpha: number): void => {
    const attr = prim.getGeometryInstanceAttributes(b.pickId);
    attr.color = cesium.ColorGeometryInstanceAttribute.toValue(
      cesium.Color.fromCssColorString(hex).withAlpha(alpha) as ColorLike, attr.color,
    );
  };

  return {
    apply(statusOf): void {
      // Skip until both primitives have rendered once: Cesium refuses getGeometryInstanceAttributes
      // before its first update pass (a hidden tab pauses rAF while the worker keeps posting snapshots).
      // The build-time identity colour holds; the next rendered frame's snapshot recolours.
      if (!wallPrim || !fillPrim || !wallPrim.ready || !fillPrim.ready) return;
      for (const b of built) {
        const status = statusOf(b.nodeId);
        const wall = assetConditionStyle(status, b.baseHex, WALL_ALPHA);
        const fill = assetConditionStyle(status, b.baseHex, FOOTPRINT_ALPHA);
        recolour(wallPrim, b, wall.hex, wall.alpha);
        recolour(fillPrim, b, fill.hex, fill.alpha);
      }
    },
    setShow(visible): void {
      if (wallPrim) wallPrim.show = visible;
      if (fillPrim) fillPrim.show = visible;
    },
    setModelMatrix(m): void {
      if (wallPrim) wallPrim.modelMatrix = m;
      if (fillPrim) fillPrim.modelMatrix = m;
    },
    dispose(): void {
      wallPrim = null;
      fillPrim = null;
      built.length = 0;
    },
  };
}
